import { useState, memo, useRef, useEffect } from 'react';

import { Link } from 'react-scroll';

import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import UseModal from 'hooks/use-modal';
import useWindowSize from 'hooks/use-windowSize';
import { useInView } from 'react-intersection-observer';

import { LectureService } from 'services/core';

import ContentOverview from 'components/common/contetnoverview/ContentOverview';
import ContentAbout from 'components/common/contentabout/ContentAbout';
import ContentBouneses from 'components/common/contentbouneses/ContentBouneses';
import ContentObjectives from 'components/common/contentobjectives/ContentObjectives';
import ContentRequirments from 'components/common/contentrequirements/ContentRequirments';
import ContentBenefits from 'components/common/contentbenefits/ContentBenefits';
import ContentFeedbackSummary from 'components/common/contentfeedbacksummary/ContentFeedbackSummary';
import ContentComments from 'components/common/contentcomments/ContentComments';
import ContentRecommendation from 'components/common/contentrecommendation/ContentRecommendation';
import ContentStickySide from 'components/common/contentstickyside/ContentStickySide';
import ContentWidthLimiter from 'components/common/contentwidthlimiter/ContentWidthLimiter';
import ContentCareerPaths from 'components/common/contentcareerpaths/ContentCareerPaths';
import Spinner from '@core/components/spinner/Spinner';
import Title from '@core/layouts/title/Title';
import Modal from '@core/components/Modal/Modal';
import CourseCertificate from 'components/pages/lecture/lecture/type/coursecertificate/CourseCertificate';
import ContentFaq from 'components/common/contentfaq/ContentFaq';

import { SUB_PAGE_TITLE } from '@core/constants/Title';
import { MOBILE_FULLSCREEN } from '@core/constants/ModalTypes';
import { isUserLoggedIn } from 'utils/authentication';

import ErrorSvg from 'assets/images/empty-search.svg';


import classes from './LectureDesktop.module.scss';



const LectureDesktop = () => {
	const [contentType] = useState('lecture');
	const isAuthenticated = isUserLoggedIn();
	const stickyRef = useRef(null);
	const { desktopDevice } = useWindowSize();
	const {
		destroyModal: destroyReviewsModal,
		show: showReviewsModal,
		showModal: handleShowReviewsModal
	} = UseModal();

	const router = useRouter();
	const [lectureId] = useState(router?.query?.slug[0]);

	const { data, isLoading, error, refetch } = useQuery(['lecture', lectureId], () =>
		LectureService(lectureId),
		{
			cacheTime: 0
		}
	);

	const { ref: reviewsRef, inView: inViewReviews } = useInView({
		threshold: 0
	});



	useEffect(() => {
		const refetchOnFocus = () => {
			setTimeout(refetch, 2000);
		};
		window.addEventListener('focus', refetchOnFocus);
		return () => {
			window.removeEventListener('focus', refetchOnFocus);
		};
	}, []);

	const [isAtTop, setIsAtTop] = useState(true);

	const stickyNavbarRef = useRef(null);

	useEffect(() => {
		const handleScroll = () => {
			if (stickyNavbarRef.current) {
				const rect = stickyNavbarRef.current.getBoundingClientRect();
				if (isAuthenticated)
					setIsAtTop(rect.top == 100);
				else
					setIsAtTop(rect.top <= 100);

			} else {
				setIsAtTop(window.scrollY === 100);
			}
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	return (
		<>
			{data && !isLoading && !error ? (
				<>
					<Title type={SUB_PAGE_TITLE} text={data?.data?.title} />
					<div className="w-100">
						<div className="d-flex flex-column">
							<ContentOverview data={data.data} contentType={'course'} refetchOverview={refetch} />

							<div
								ref={stickyNavbarRef}
								className={`d-none d-xl-block ${classes['content-page-sticky-navbar']} ${!isAuthenticated ? classes['guest'] : ''}`}
								style={isAtTop ? { zIndex: '999' } : {}}
							>
								<ul className="d-flex flex-row align-items-center justify-content-center">
									<li>
										<Link
											activeClass={classes['active-content-section']}
											offset={-78}
											delay={0}
											spy
											to="about">
											About
										</Link>
									</li>
									<li>
										<Link
											activeClass={classes['active-content-section']}
											offset={-200}
											delay={0}
											spy
											to="certificate">
											Certificate
										</Link>
									</li>
									{data.data?.learning_objectives.length > 0 && (
										<li>
											<Link
												activeClass={classes['active-content-section']}
												offset={-78}
												delay={0}
												spy
												to="objectives">
												Contents
											</Link>
										</li>
									)}
									<li>
										<Link
											activeClass={classes['active-content-section']}
											offset={-78}
											delay={0}
											spy
											to="benefits">
											Benefits
										</Link>
									</li>
									<li>
										<Link
											activeClass={classes['active-content-section']}
											offset={-78}
											delay={0}
											spy
											to="reviews">
											Reviews
										</Link>
									</li>
								</ul>
							</div>
							{/* <div className='container'></div> */}
							<div style={{ position: 'relative' }}>
								<div
									className="container d-none d-xl-block"
									style={{
										position: 'sticky',
										top: '140px',
										marginTop: '80px',
										marginBottom: '80px'
									}}>
									<div className="row d-none d-xl-flex">
										<div className="col-8"></div>
										<div className="col-4" ref={stickyRef}>
											<ContentStickySide data={data.data} refetchOverview={refetch} />
										</div>
									</div>
								</div>
								<div
									className={`w-100 ${classes['content-page-data-wrapper']}`}
									style={{
										marginTop: stickyRef?.current?.offsetHeight
											? `-${stickyRef?.current?.offsetHeight + 120}px`
											: '-400px'
									}}>
									<div className="">
										<div id="about" className={`${classes['content-page-about-section-wrapper']}`}>
											<ContentWidthLimiter>
												<ContentAbout
													categories={data?.data?.categories}
													description={data?.data?.description}
													title={data?.data?.title}
												/>
											</ContentWidthLimiter>
										</div>
										<div
											id="certificate"
											className={`${classes['content-page-certificate-section-wrapper']}`}>
											<ContentWidthLimiter>
												<CourseCertificate />
											</ContentWidthLimiter>
										</div>
										{/* <div
											id="bouneses"
											className={`${classes['content-page-bouneses-section-wrapper']}`}>
											<ContentWidthLimiter>
												<ContentBouneses contentType={'course'} />
											</ContentWidthLimiter>
										</div> */}
										{(data.data?.learning_objectives_description ||
											data.data?.learning_objectives?.length > 0 || data.data?.versioning) && (
												<div
													id="objectives"
													className={`${classes['content-page-objectives-section-wrapper']}`}>
													<ContentWidthLimiter>
														<ContentObjectives
															title={data?.data?.title}
															lectureId={lectureId}
															versioning={data.data?.versioning}
															description={data.data?.learning_objectives_description}
															learning_objectives={data.data?.learning_objectives}
														/>
													</ContentWidthLimiter>
												</div>
											)}

										{(data.data?.career_paths?.length > 0 && (
											<div
												id="career-paths"
												className={`${classes['content-page-career-section-wrapper']}`}>
												<ContentWidthLimiter>
													<ContentCareerPaths careerPaths={data.data?.career_paths} />
												</ContentWidthLimiter>
											</div>
										))}
										<div
											id="benefits"
											className={`${classes['content-page-benefits-section-wrapper']}`}>
											<ContentWidthLimiter>
												<ContentBenefits />
											</ContentWidthLimiter>
										</div>

										{!error && data?.data?.status !== 'coming_soon' && (
											<div
												id="reviews"
												ref={reviewsRef}
												className={`${classes['content-page-reviews-section-wrapper']} container`}>
												<ContentWidthLimiter>
													{inViewReviews && (
														<>
															<ContentFeedbackSummary
																contentType={contentType}
																hasEmptyState={true}
															/>
															<ContentComments
																className="my-5"
																contentType={contentType}
																tabSetterHandler={handleShowReviewsModal}
																deviceType={desktopDevice ? 'desktop' : 'mobile'}
															/>
														</>
													)}
												</ContentWidthLimiter>
											</div>
										)}
									</div>
								</div>
							</div>
							<div className={`${classes['content-page-recommendation-section-wrapper']}`}>
								<div className="container">
									<ContentRecommendation contentId={lectureId} content_type="course" />
								</div>
							</div>
							<div className={`${classes['content-page-faq-section-wrapper']}`}>
								<div className="container">
									<ContentFaq contentName={data?.data?.title} />
								</div>
							</div>
						</div>
					</div>
				</>
			) : isLoading && !error ? (
				<div className="d-flex flex-row align-items-center justify-content-center w-100 py-5 my-5">
					<Spinner />
				</div>
			) : (
				<div
					className={`${classes['lecture-overview-error']} d-flex flex-column align-items-center justify-content-center w-100`}>
					<ErrorSvg />
					<h1>Oh oh!</h1>
					<p>Something went wrong! please reload the page.</p>
				</div>
			)}
			<Modal
				closeHandler={destroyReviewsModal}
				show={showReviewsModal}
				title={'Reviews'}
				type={MOBILE_FULLSCREEN}>
				<div style={{ minWidth: '70vw' }}>
					<ContentFeedbackSummary contentType={contentType} hasEmptyState={true} />
					<ContentComments className="my-5" contentType={contentType} listDirection="column" />
				</div>
			</Modal>
		</>
	);
};

export default memo(LectureDesktop);
